/**
 * Web hooks for default liability waiver operations
 * Pattern: useState/useCallback (same approach as useWaivers.ts)
 */

import { useEffect, useState, useCallback } from 'react';

import type { DefaultWaiverTemplate } from '../types/damageWaiver.types';

import {
  getLatestDefaultWaiver,
  applyDefaultWaiverToCompany,
  companyHasWaiverTemplate,
  getDefaultWaiverTemplates,
  createDefaultWaiverTemplate,
  updateDefaultWaiverTemplate,
  getNextDefaultWaiverVersion,
} from '../services/defaultWaiver';

/**
 * Hook: Check if a default waiver template is available
 */
export function useDefaultWaiverAvailable() {
  const [data, setData] = useState<DefaultWaiverTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getLatestDefaultWaiver()
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, []);

  return { data, isLoading, error };
}

/**
 * Hook: Check if a company already has any waiver template
 */
export function useCompanyHasWaiver(companyId: string | null | undefined) {
  const [data, setData] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) {
      setData(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    companyHasWaiverTemplate(companyId)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [companyId]);

  return { data, isLoading, error };
}

/**
 * Hook: Apply the default waiver template to a company
 */
export function useApplyDefaultWaiver() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const apply = useCallback(async (companyId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await applyDefaultWaiverToCompany(companyId);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { apply, isLoading, error };
}

/**
 * Hook: List all default waiver template versions (for admin page)
 */
export function useDefaultWaiverTemplates() {
  const [data, setData] = useState<DefaultWaiverTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(() => {
    setIsLoading(true);
    setError(null);
    getDefaultWaiverTemplates()
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

/**
 * Hook: Save (create or update) a default waiver template
 */
export function useSaveDefaultWaiver() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(async (input: { version: string; name?: string; content_html: string }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await createDefaultWaiverTemplate(input);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: string, updates: { name?: string; content_html?: string; is_active?: boolean }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await updateDefaultWaiverTemplate(id, updates);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getNextVersion = useCallback(async () => {
    return getNextDefaultWaiverVersion();
  }, []);

  return { create, update, getNextVersion, isLoading, error };
}
